import { ensureRenderableImage } from './heic';

// Brand Hub "Fill from screenshots": shrink each chosen screenshot in the
// browser before it is sent, so a phone's 3–6 full-resolution screenshots stay
// well under the backend's per-image (3.75 MB) and total (15 MB) caps and upload
// quickly on mobile data. Nothing here is stored or uploaded — the caller
// sends the result once and drops it.

export const MAX_SCREENSHOTS = 6;
export const MAX_SIDE = 1600;
export const JPEG_QUALITY = 0.85;

// Scale (w, h) so the longest side is at most maxSide. Never upscales.
export function fitWithin(width, height, maxSide = MAX_SIDE) {
  const longest = Math.max(width, height);
  if (!(longest > maxSide)) return { width, height };
  const scale = maxSide / longest;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

// createImageBitmap where the browser has it, else an <img> from an object URL.
async function decode(file) {
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file); } catch (_) { /* fall back to <img> */ }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('decode_failed'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

// One file → { mediaType, data } (data = base64, no data: prefix), JPEG at
// most MAX_SIDE on the longest side. Throws if the image can't be decoded.
export async function downscaleImage(file, { maxSide = MAX_SIDE, quality = JPEG_QUALITY } = {}) {
  const source = await decode(await ensureRenderableImage(file));
  const w = source.width || source.naturalWidth;
  const h = source.height || source.naturalHeight;
  if (!w || !h) throw new Error('decode_failed');
  const size = fitWithin(w, h, maxSide);
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no_canvas');
  ctx.fillStyle = '#fff'; // transparent PNG areas would turn black as JPEG
  ctx.fillRect(0, 0, size.width, size.height);
  ctx.drawImage(source, 0, 0, size.width, size.height);
  if (typeof source.close === 'function') source.close();
  const url = canvas.toDataURL('image/jpeg', quality);
  const m = /^data:(image\/(?:jpeg|png|webp));base64,(.+)$/.exec(url || '');
  if (!m) throw new Error('encode_failed');
  return { mediaType: m[1], data: m[2] };
}

// files → { images, skipped }. A file that can't be decoded is skipped (its
// name goes in `skipped`) instead of failing the whole batch. One at a time,
// so a phone never holds six decoded full-size screenshots at once.
export async function prepareScreenshots(files, { downscale = downscaleImage } = {}) {
  const images = [];
  const skipped = [];
  for (const file of Array.from(files || []).slice(0, MAX_SCREENSHOTS)) {
    try {
      images.push(await downscale(file));
    } catch (_) {
      skipped.push(file?.name || 'image');
    }
  }
  return { images, skipped };
}

// "Read 4 screenshots · found 3 posts"
export function screenshotResultLine(s) {
  if (!s) return '';
  const n = Number(s.imagesRead) || 0;
  const p = Number(s.postsRead) || 0;
  return `Read ${n} screenshot${n === 1 ? '' : 's'} · found ${p} post${p === 1 ? '' : 's'}`;
}
